import { useState } from "react";
import { FileDown, Loader2, CheckCircle2, Circle, X, Gavel, Sparkles, TrendingUp, ShieldCheck, FileSignature } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, openSigned } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { SignActions } from "@/components/SignActions";
import { SigningRequestBadges } from "@/components/SigningRequestBadges";
import { nb, nok, formatDate, formatDateTime } from "@/lib/format";

const STATUS = {
  venter_vedtak: { label: "Venter på vedtak", cls: "border-amber-500/30 bg-amber-500/10 text-amber-600" },
  venter_signering: { label: "Venter på signering", cls: "border-blue-500/30 bg-blue-500/10 text-blue-600" },
  gjennomfort: { label: "Gjennomført", cls: "border-emerald-500/30 bg-emerald-500/10 text-emerald-600" },
  signert: { label: "Signert", cls: "border-emerald-500/30 bg-emerald-500/10 text-emerald-600" },
  kansellert: { label: "Kansellert", cls: "border-border bg-muted text-muted-foreground" },
};

export function AgreementsTab({ agreements, onChanged }) {
  const [busy, setBusy] = useState(null);

  const act = async (id, fn, okMsg) => {
    setBusy(id);
    try {
      const res = await fn();
      toast.success(typeof okMsg === "function" ? okMsg(res) : okMsg);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  if (!agreements || agreements.length === 0) {
    return <p className="p-8 text-center text-sm text-muted-foreground">Ingen avtaler ennå. Aksjonæravtaler lages under «Dokumenter → Lag aksjonæravtale». Aksjekjøp/emisjon lages under «Kjøp / salg».</p>;
  }

  return (
    <div className="divide-y divide-border/60">
      {agreements.map((a) => {
        const meta = STATUS[a.status] || STATUS.kansellert;
        const isEm = a.kind === "emisjon";
        const isSh = a.kind === "aksjonaeravtale";
        const total = (a.shares || 0) * (a.price_per_share || 0);
        return (
          <div key={a.id} data-testid={`agreement-${a.id}`} className="p-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="flex items-center gap-1.5 font-medium">
                  {isSh ? <FileSignature className="h-4 w-4 text-primary" /> : isEm ? <TrendingUp className="h-4 w-4 text-primary" /> : <Sparkles className="h-4 w-4 text-primary" />}
                  {a.title || (isSh ? "Aksjonæravtale" : isEm ? "Emisjon" : "Aksjekjøpsavtale")}
                </p>
                <p className="text-xs text-muted-foreground">
                  {isSh
                    ? `${(a.parties || []).length} parter · ${formatDate(a.date)}`
                    : `${nb(a.shares)} ${a.share_class} · ${nok(a.price_per_share)}/aksje · totalt ${nok(total)} · ${formatDate(a.date)}`}
                </p>
              </div>
              <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${meta.cls}`} data-testid={`agreement-status-${a.id}`}>{meta.label}</span>
            </div>

            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs">
              {(a.parties || []).map((p, i) => (
                <span key={i} className="flex items-center gap-1.5">
                  {p.signed ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Circle className="h-4 w-4 text-muted-foreground/40" />}
                  {isSh ? p.name : `${p.role} (${p.name})`}: {p.signed ? `signert ${formatDateTime(p.signed_at)}${p.method === "bankid" ? " (BankID)" : ""}` : "avventer signering"}
                </span>
              ))}
            </div>

            {a.resolution ? (
              <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground"><Gavel className="h-3.5 w-3.5" /> Vedtak {formatDateTime(a.resolution.decided_at)} av {a.resolution.decided_by}{a.resolution.note ? ` – ${a.resolution.note}` : ""}</p>
            ) : null}

            {a.status === "venter_signering" ? <SigningRequestBadges entityType="agreement" entityId={a.id} /> : null}

            <div className="mt-3 flex flex-wrap gap-2">
              {isEm && a.status === "venter_vedtak" ? (
                <Button size="sm" className="gap-1.5" disabled={busy === a.id} data-testid={`resolve-agreement-${a.id}`}
                  onClick={() => act(a.id, () => api.post(`/agreements/${a.id}/resolution`, { note: "Styrevedtak om kapitalforhøyelse" }), "Vedtak registrert – klar for signering")}>
                  {busy === a.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gavel className="h-4 w-4" />} Registrer vedtak
                </Button>
              ) : null}
              {a.status === "venter_signering" ? (
                <SignActions entityType="agreement" entityId={a.id} onChanged={onChanged} />
              ) : null}
              <Button size="sm" variant="outline" className="gap-1.5" data-testid={`agreement-pdf-${a.id}`}
                onClick={() => openSigned(`/agreements/${a.id}/pdf-link`).catch(() => toast.error("Kunne ikke åpne avtalen"))}>
                <FileDown className="h-4 w-4" /> Avtale (PDF)
              </Button>
              {a.bankid_signed_path ? (
                <Button size="sm" variant="outline" className="gap-1.5" data-testid={`agreement-bankid-pdf-${a.id}`}
                  onClick={() => openSigned(`/signing/agreement/${a.id}/signed-link`).catch(() => toast.error("Kunne ikke åpne det signerte dokumentet"))}>
                  <ShieldCheck className="h-4 w-4 text-emerald-600" /> BankID-signert PDF
                </Button>
              ) : null}
              {a.status !== "gjennomfort" && a.status !== "kansellert" ? (
                <Button size="sm" variant="ghost" className="gap-1.5 text-muted-foreground" data-testid={`cancel-agreement-${a.id}`}
                  onClick={() => act(a.id, () => api.post(`/agreements/${a.id}/cancel`), "Avtale kansellert")}>
                  <X className="h-4 w-4" /> Kanseller
                </Button>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}
